import { reactive } from 'vue'
import { auth } from '@/firebase'

// No student/event payload is persisted in browser storage. Retain failed jobs
// only in this session, with a fixed cap and stable IDs for safe server retries.
export const auditHealth = reactive({
  pending: 0,
  failed: 0,
  dropped: 0,
  lastCode: '',
  lastReference: ''
})
const jobs = new Map()
const TRANSIENT = /unavailable|deadline-exceeded|network|internal|resource-exhausted/
function refresh() {
  auditHealth.pending = [...jobs.values()].filter((j) => j.state === 'pending').length
  auditHealth.failed = [...jobs.values()].filter((j) => j.state === 'failed').length
}
function deadline(promise) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(
      () =>
        reject(
          Object.assign(new Error('Activity service timed out'), { code: 'deadline-exceeded' })
        ),
      4000
    )
    promise.then(resolve, reject).finally(() => clearTimeout(timer))
  })
}
async function run(job) {
  if (job.running) return job.running
  job.state = 'pending'
  refresh()
  job.running = (async () => {
    for (let attempt = 0; attempt < 3; attempt++) {
      if (auth.currentUser?.uid !== job.ownerUid || !jobs.has(job.id)) {
        jobs.delete(job.id)
        refresh()
        return { recorded: false, cancelled: true }
      }
      try {
        const result = await deadline(job.send())
        if (result?.data?.recorded !== true)
          throw Object.assign(new Error('Unconfirmed audit write'), { code: 'internal' })
        jobs.delete(job.id)
        refresh()
        return { recorded: true }
      } catch (error) {
        if (!jobs.has(job.id)) return { recorded: false, cancelled: true }
        auditHealth.lastCode = String(error?.code || 'unavailable').slice(0, 80)
        auditHealth.lastReference =
          String(error?.message || '').match(/\bERR-[A-F0-9]{6}\b/)?.[0] || job.id
        if (attempt < 2 && TRANSIENT.test(auditHealth.lastCode)) {
          await new Promise((resolve) => setTimeout(resolve, 300 * (attempt + 1)))
          continue
        }
        job.state = 'failed'
        refresh()
        return { recorded: false }
      }
    }
  })().finally(() => {
    job.running = null
  })
  return job.running
}
export function deliverAudit({ id, ownerUid, send }) {
  if (!ownerUid) return Promise.resolve({ recorded: false })
  const existing = jobs.get(id)
  if (existing) return run(existing)
  if (jobs.size >= 100) {
    auditHealth.dropped++
    return Promise.resolve({ recorded: false, overflow: true })
  }
  const job = { id, ownerUid, send, state: 'pending', running: null }
  jobs.set(id, job)
  return run(job)
}
export async function retryAuditDelivery() {
  await Promise.all([...jobs.values()].filter((j) => j.state === 'failed').map(run))
}
export function clearAuditDelivery() {
  jobs.clear()
  Object.assign(auditHealth, { pending: 0, failed: 0, dropped: 0, lastCode: '', lastReference: '' })
}
